import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Input, Text } from '@/components/ui';
import { Switch } from '@/components/ui/Switch';
import { TextArea } from '@/components/ui/Textarea';
import { SaveIcon } from 'lucide-react';
import { useSettings, SettingsFormData } from '@/hooks/useSettings';
import { useTranslation } from 'react-i18next';
import { Loader } from '@/components/Loader';

const settingsSchema = (t: any) =>
  z.object({
    movidesk_api_url: z.string().url(t('settings.invalidUrl')).or(z.string().length(0)),
    movidesk_api_key: z.string(),
    notification_email_host: z.string(),
    notification_email_port: z
      .string()
      .regex(/^\d+$/, t('settings.mustBeNumber'))
      .or(z.string().length(0)),
    notification_email_user: z.string(),
    notification_email_password: z.string(),
    notification_email_to: z.string(),
    enable_notifications: z.boolean(),
  });

type SettingsForm = z.infer<ReturnType<typeof settingsSchema>>;

export function SettingsPage() {
  const { t } = useTranslation();
  const { isLoading, isSaving, getFormData, saveSettings } = useSettings();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<SettingsForm>({
    resolver: zodResolver(settingsSchema(t)),
    defaultValues: {
      movidesk_api_url: '',
      movidesk_api_key: '',
      notification_email_host: '',
      notification_email_port: '587',
      notification_email_user: '',
      notification_email_password: '',
      notification_email_to: '',
      enable_notifications: false,
    },
  });

  const enableNotifications = watch('enable_notifications');

  useEffect(() => {
    if (!isLoading) {
      const formData = getFormData();
      reset(formData);
    }
  }, [isLoading, getFormData, reset]);

  async function onSubmit(data: SettingsForm) {
    await saveSettings(data as SettingsFormData);
  }

  return (
    <div className="gap-2xl flex flex-1 flex-col">
      <Loader isLoading={isLoading} />
      <header className="flex h-fit w-full items-center justify-between">
        <Text as="h1" variant="heading">
          {t('settings.title')}
        </Text>
        <div>
          <Button onClick={handleSubmit(onSubmit)} loading={isSaving} disabled={isLoading}>
            <SaveIcon size={20} />
            {t('common.save')}
          </Button>
        </div>
      </header>

      <section>
        <Text as="strong" size="lg" strong>
          {t('settings.movideskSectionTitle')}
        </Text>

        <div className="gap-md mt-md flex flex-col">
          <Input
            {...register('movidesk_api_url')}
            label={t('settings.movideskApiUrl')}
            placeholder={t('settings.movideskApiUrlPlaceholder')}
            error={errors.movidesk_api_url?.message}
            disabled={isLoading}
          />
          <Input
            {...register('movidesk_api_key')}
            label={t('settings.movideskApiKey')}
            placeholder={t('settings.movideskApiKeyPlaceholder')}
            error={errors.movidesk_api_key?.message}
            disabled={isLoading}
          />
        </div>
      </section>

      <section>
        <Text as="strong" size="lg" strong>
          {t('settings.notificationSectionTitle')}
        </Text>

        <div className="gap-md mt-md flex items-center">
          <Text as="label" size="md" htmlFor="enable-notifications">
            {t('settings.enableNotifications')}
          </Text>
          <Switch
            id="enable-notifications"
            checked={enableNotifications}
            onCheckedChange={(checked) => setValue('enable_notifications', checked)}
            disabled={isLoading}
          />
        </div>

        <div className="gap-md mt-md grid grid-cols-2">
          <Input
            {...register('notification_email_host')}
            label={t('settings.notificationEmailHost')}
            placeholder={t('settings.notificationEmailHostPlaceholder')}
            error={errors.notification_email_host?.message}
            disabled={isLoading || !enableNotifications}
          />
          <Input
            {...register('notification_email_port')}
            label={t('settings.notificationEmailPort')}
            placeholder={t('settings.notificationEmailPortPlaceholder')}
            error={errors.notification_email_port?.message}
            disabled={isLoading || !enableNotifications}
          />
          <Input
            {...register('notification_email_user')}
            label={t('settings.notificationEmailUser')}
            placeholder={t('settings.notificationEmailUserPlaceholder')}
            error={errors.notification_email_user?.message}
            disabled={isLoading || !enableNotifications}
          />
          <Input
            {...register('notification_email_password')}
            type="password"
            label={t('settings.notificationEmailPassword')}
            placeholder={t('settings.notificationEmailPasswordPlaceholder')}
            error={errors.notification_email_password?.message}
            disabled={isLoading || !enableNotifications}
          />
          <TextArea
            {...register('notification_email_to')}
            label={t('settings.notificationEmailTo')}
            placeholder={t('settings.notificationEmailToPlaceholder')}
            className="col-span-2"
            error={errors.notification_email_to?.message}
            disabled={isLoading || !enableNotifications}
          />
        </div>
      </section>
    </div>
  );
}
